import { clearStoredTokens, useAuthTokens } from "../hooks/use-auth-tokens.js";
import { FIVE_MINUTES, SIXTY_SECONDS } from "../util/tokens.js";
import { useRefreshExpiredToken } from "../hooks/use-refresh-expired-token.js";
import { AUTH_FLOW, initiateLoginChallenge } from "../util/login.js";
import { AuthHeartbeat } from "./AuthHeartbeat.js";
import { createContext, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Body1, Button, Modal, Stack } from "@myphx-engineering/phx-design-system";
import { useIdle } from "@myphx-engineering/phx-design-system/hooks";
import { jsx, jsxs } from "react/jsx-runtime";

//#region src/client/components/AuthProvider.tsx
const AuthContext = createContext(null);
const SIGNOUT_DELAY = 500;
const AuthProvider = ({ children, authUrl, autoLogout = FIVE_MINUTES, clientId, logoutUrl = "/logout", redirectUri, tokenUrl, rootUrl, disableHeartbeat, disableAuthFlows = false, defaultState = {} }) => {
	const { t } = useTranslation("auth");
	const tokens = useAuthTokens();
	const isIdle = useIdle(autoLogout, {
		initialState: false,
		events: ["click", "keypress"]
	});
	const showIdleTimeout = useIdle(autoLogout - SIXTY_SECONDS, {
		initialState: false,
		events: ["click", "keypress"]
	});
	const expiry = tokens?.expMs;
	const refreshToken = tokens?.refreshToken;
	const hasAccessToken = Boolean(tokens?.accessToken);
	const isAccessTokenExpired = hasAccessToken && (expiry === void 0 || expiry < Date.now());
	const isAuthenticated = hasAccessToken && (!isAccessTokenExpired || Boolean(refreshToken));
	useRefreshExpiredToken({
		clientId,
		isAccessTokenExpired,
		refreshToken,
		tokenUrl
	});
	const signIn = useCallback((returnUrl = "", state = {}) => {
		if (disableAuthFlows) return;
		initiateLoginChallenge({
			authUrl,
			clientId,
			redirectUri,
			returnUrl: returnUrl === "" ? redirectUri : returnUrl,
			state: {
				...defaultState,
				...state
			}
		});
	}, [
		authUrl,
		clientId,
		defaultState,
		disableAuthFlows,
		redirectUri
	]);
	const signUp = useCallback((returnUrl = "", state = {}) => {
		signIn(returnUrl, {
			...state,
			flow: AUTH_FLOW.CreateAccount
		});
	}, [signIn]);
	const signOut = useCallback((returnUrl) => {
		clearStoredTokens(true);
		const timeout = setTimeout(() => {
			if (returnUrl) window.location.href = returnUrl;
			else window.location.href = rootUrl;
		}, SIGNOUT_DELAY);
		return () => {
			clearTimeout(timeout);
		};
	}, [rootUrl]);
	useEffect(() => {
		if (isIdle && isAuthenticated) window.location.href = logoutUrl;
	}, [
		isAuthenticated,
		isIdle,
		logoutUrl,
		signOut
	]);
	const ctx = {
		authUrl,
		clientId,
		disableAuthFlows,
		isAuthenticated,
		redirectUri,
		rootUrl,
		signIn,
		signOut,
		signUp,
		tokens,
		tokenUrl,
		user: tokens?.accessToken ? {
			id: tokens.accessToken.sub,
			token: tokens?.accessToken
		} : null
	};
	const pollForRefresh = expiry && refreshToken;
	return /* @__PURE__ */ jsxs(AuthContext.Provider, {
		value: ctx,
		children: [
			children,
			!disableHeartbeat && pollForRefresh && /* @__PURE__ */ jsx(AuthHeartbeat, {
				clientId,
				expiry,
				logoutUrl,
				refreshToken,
				tokenUrl
			}),
			isAuthenticated && showIdleTimeout ? /* @__PURE__ */ jsx(Modal, {
				opened: true,
				onClose: () => {},
				title: t("idle.timeout.heading"),
				children: /* @__PURE__ */ jsxs(Stack, {
					gap: "sm",
					children: [/* @__PURE__ */ jsx(Body1, { children: t("idle.timeout.message") }), /* @__PURE__ */ jsx(Stack, {
						gap: "xs",
						children: /* @__PURE__ */ jsx(Button, {
							fullWidth: true,
							children: t("idle.timeout.cta")
						})
					})]
				})
			}) : null
		]
	});
};

//#endregion
export { AuthContext, AuthProvider };